import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import { getPhotos, getThumbnails, deletePhoto, getAllPhotos } from './photoDB';
import { getGlobalDealerName } from './globalState';
import { useGetBrand } from './BrandsContext';
import { MAX_PHOTOS } from './limits';


function PhotoGallery() {
  /*
  PhotoGallery component allows users to view, select, and delete photos.
  It displays all photos stored in IndexedDB, allows selection of up to
  MAX_PHOTOS photos, and provides options to delete photos or submit selected photos.

  Routing on submit depends on how the user arrived:
    source === 'license-plate'  →  /license-plate-confirm  (plate upload flow)
    source === 'backgrounds'    →  /photo-confirm           (dealership photo flow)
  */
  const location = useLocation();
  const navigate = useNavigate();
  const initialPhotoKeys = location.state?.photoKeys || [];
  const initialSelectedPhotoKeys = location.state?.selectedPhotoKeys || [];
  const phototype = location.state?.source || 'backgrounds';
  // 'frame' when photographing a custom frame via UploadFrame;
  // 'plate' (default) for normal license plate photography.
  const assetType = location.state?.assetType || 'plate';

  const [photoKeys, setPhotoKeys] = useState(initialPhotoKeys);
  // Object URLs for preview — created from Blobs, revoked on cleanup to avoid leaks.
  const [photoURLs, setPhotoURLs] = useState([]);
  // Map<key, objectURL> so handleDeletePhoto can find a URL by key without
  // depending on array index (which breaks under rapid back-to-back deletes).
  const objectURLsRef = useRef(new Map());
  // Set to true by handleDeletePhoto so the photoKeys effect skips its cleanup
  // and re-fetch (the delete handler already managed URLs surgically).
  const skipNextFetchRef = useRef(false);
  const [selectedPhotoKeys, setSelectedPhotoKeys] = useState(initialSelectedPhotoKeys);
  const [showVehiclePaths, setShowVehiclePaths] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const getBrand = useGetBrand();
  const vehiclePathOverlay = getBrand(getGlobalDealerName())?.overlay;

  useEffect(() => {
    async function loadKeysIfNeeded() {
      if (!initialPhotoKeys.length) {
        const keys = await getAllPhotos();
        setPhotoKeys(keys);
      }
    }
    loadKeysIfNeeded();
  }, []);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      // handleDeletePhoto already updated objectURLsRef and photoURLs surgically.
      // Skip the full re-fetch; just reset the flag and return the standard cleanup.
      skipNextFetchRef.current = false;
      return () => {
        if (!skipNextFetchRef.current) {
          objectURLsRef.current.forEach(url => URL.revokeObjectURL(url));
        }
      };
    }
    async function fetchThumbnails() {
      const thumbs = await getThumbnails(photoKeys);
      objectURLsRef.current.forEach(url => URL.revokeObjectURL(url));
      const newMap = new Map();
      const urls = thumbs.map((blob, i) => {
        const url = URL.createObjectURL(blob);
        newMap.set(photoKeys[i], url);
        return url;
      });
      objectURLsRef.current = newMap;
      setPhotoURLs(urls);
    }
    if (photoKeys.length) fetchThumbnails();
    else {
      objectURLsRef.current.forEach(url => URL.revokeObjectURL(url));
      objectURLsRef.current = new Map();
      setPhotoURLs([]);
    }
    return () => {
      if (!skipNextFetchRef.current) {
        objectURLsRef.current.forEach(url => URL.revokeObjectURL(url));
      }
    };
  }, [photoKeys]);

  const togglePhotoSelection = (photoKey) => {
    setSelectedPhotoKeys((prevSelected) => {
      if (prevSelected.includes(photoKey)) {
        return prevSelected.filter((k) => k !== photoKey);
      } else if (prevSelected.length < MAX_PHOTOS) {
        return [...prevSelected, photoKey];
      } else {
        alert(`You can only select up to ${MAX_PHOTOS} photos.`);
        return prevSelected;
      }
    });
  };

  const handleDeletePhoto = async (key) => {
    await deletePhoto(key);
    // Look up the URL by key — no index needed, so rapid back-to-back deletes
    // can't compute a wrong position from a stale photoKeys closure.
    const url = objectURLsRef.current.get(key);
    if (url !== undefined) {
      URL.revokeObjectURL(url);
      objectURLsRef.current.delete(key);
      setPhotoURLs(prev => prev.filter(u => u !== url));
    }
    // Signal the photoKeys effect to skip its cleanup and re-fetch — URLs are
    // already correct after the surgical update above.
    skipNextFetchRef.current = true;
    setPhotoKeys(prev => prev.filter(k => k !== key));
    setSelectedPhotoKeys(prev => prev.filter(k => k !== key));
  };

  const handleSubmitPhotos = async () => {
    if (selectedPhotoKeys.length === 0) {
      alert('Please select at least one photo to submit.');
      return;
    }

    if (phototype === 'license-plate') {
      // Convert selected IndexedDB photos to File objects so
      // LicencePlateConfirm can send them as multipart/form-data.
      setIsSubmitting(true);
      try {
        const blobs = await getPhotos(selectedPhotoKeys);
        const files = blobs.map((blob, i) =>
          new File([blob], `plate_${i + 1}.jpg`, { type: blob.type || 'image/jpeg' })
        );
        navigate('/license-plate-confirm', {
          state: {
            files,
            // Pass all session keys so ThankYou can clean up IndexedDB
            allPhotoKeys: photoKeys,
            assetType,
          },
        });
      } catch (err) {
        console.error('Failed to prepare plate photos:', err);
        alert('An error occurred preparing your photos. Please try again.');
        setIsSubmitting(false);
      }
    } else {
      navigate('/photo-confirm', {
        state: {
          photoKeys,
          selectedPhotoKeys,
        }
      });
    }
  };

  return (
    <div className="PhotoGallery">
      <h1 className="PhotoGallery-title">Select Up To {MAX_PHOTOS} Photos To Send:</h1>
      <div className="PhotoGallery-grid">
        {photoURLs.map((photo, idx) => {
          const key = photoKeys[idx];
          return (
            <div
              key={key}
              className={`PhotoGallery-item ${selectedPhotoKeys.includes(key) ? 'selected' : ''}`}
              onClick={() => togglePhotoSelection(key)}
              style={{
                position: 'relative',
                aspectRatio: '4 / 3',
                overflow: 'hidden',
              }}
            >
              <img
                src={photo}
                alt={`Photo ${idx + 1}`}
                className="PhotoGallery-image"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  aspectRatio: '4 / 3'
                }}
              />
              {vehiclePathOverlay && showVehiclePaths && (
                <img
                  src={vehiclePathOverlay}
                  alt="Vehicle Path Overlay"
                  className="PhotoGallery-path-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover', // Crop overlay to 4:3
                    aspectRatio: '4 / 3',
                    pointerEvents: 'none',
                    zIndex: 2,
                    opacity: 0.7,
                  }}
                />
              )}
              <button
                className="PhotoGallery-delete"
                onClick={e => {
                  e.stopPropagation();
                  handleDeletePhoto(key);
                }}
                aria-label="Delete photo"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <div className="PhotoGallery-actions">
        {vehiclePathOverlay && phototype === 'backgrounds' && (
          <button
            className="App-button"
            onClick={() => setShowVehiclePaths(prev => !prev)}
          >
            {showVehiclePaths ? 'Hide Vehicle Overlay' : 'Show Vehicle Overlay'}
          </button>
        )}
        <button
          className="App-button"
          onClick={() =>
            navigate(phototype === 'license-plate' ? '/license-plate-photography' : '/photography', {
              state: phototype === 'license-plate' ? { assetType } : { photoKeys }
            })
          }
        >
          Back to Camera
        </button>
        <button className="App-button" onClick={handleSubmitPhotos} disabled={isSubmitting}>
          Submit Photos
        </button>
      </div>
    </div>
  );
}

export default PhotoGallery;
